import { useEffect, useState, type CSSProperties } from 'react';
import { Badge, Button, Icon, IconButton, Mono } from './primitives';
import { Card } from './card';
import { InlineStatus, LoadError, useToast } from './feedback';
import { RequestStatus } from './requests';
import { supabase } from '../lib/supabase';
import { addDays, addDaysToKey, fmtDayKey, orgDateKey } from '../lib/dates';
import { elapsedOf, fmtTime } from '../lib/dayclock';
import { fmtDuration } from '../lib/duration';
import { useMediaQuery } from '../lib/useMediaQuery';
import { isModuleEnabled } from '../lib/roles';
import { cancelCorrection, correctionSummary, type Correction } from '../lib/timeCorrections';
import { cancelDayEdit, sessionsText, type DayEdit } from '../lib/editDay';
import {
  cancelClaim,
  fmtHours,
  listCategories,
  type Category,
  type CompHours,
} from '../lib/compHours';

/** Only the fields this log reads off an attendance row. */
type Attendance = { date: string; status: 'present' | 'wfh' | 'leave' | 'absent' | 'holiday' };

// ─────────────────────────────────────────────────────────────────────────────
// Check-in history — check-in / check-out timings per day, for HR verification
// and for the person's own reconciliation.
// Derived from the person's day-clock time_entries (project_id is null):
// created_at is the true arrival (never mutated), ended_at the checkout.
// HR can read these via the timesheets=full RLS policy.
// ─────────────────────────────────────────────────────────────────────────────
type DayClockEntry = {
  id: string;
  created_at: string;
  started_at: string;
  ended_at: string | null;
  seconds: number;
  status: 'running' | 'paused' | 'logged';
  system_closed: boolean;
};

// `dayKey` and a private `orgDateKey` used to live here — byte-for-byte copies of
// isoDay/orgDateKey in lib/dates, in the one component on this screen that did not
// use them. Deleted: docs/UI-CONVENTIONS.md §2 exists because four definitions of
// "today" is how the heatmap came to render the previous day.
// keyLabel and orgDateLabel lived here after them: two private date formatters
// that followed the browser's locale, so one log read "Mon, Sep 28" on one HR
// machine and "lun. 28 sept." on the next. A day is fmtDayKey.
const dayLabel = (k: string): string => fmtDayKey(k, { weekday: true });

type LeaveSpan = { start_date: string; end_date: string; leave_type_id: string };
type PauseRow = { id: string; entry_id: string; paused_at: string; resumed_at: string | null };
// The day's requests, read beside the clock so a corrected or credited day
// explains itself instead of reading as a long pause.
type CorrectionRow = Pick<
  Correction,
  | 'id'
  | 'entry_id'
  | 'work_date'
  | 'current_seconds'
  | 'requested_seconds'
  | 'requested_in'
  | 'requested_out'
  | 'reason'
  | 'status'
>;
type ClaimRow = Pick<CompHours, 'id' | 'date' | 'hours' | 'category' | 'status'>;
// Edit day (20261103120000): a whole-day change, made directly or asked for.
type DayEditRow = Pick<
  DayEdit,
  'id' | 'work_date' | 'kind' | 'status' | 'reason' | 'decline_reason' | 'proposed' | 'applied'
>;
// time_allocation (20261014190000): a logged day's worked time split by project
// timer. project_id null is the day's unallocated rest.
type AllocRow = {
  work_date: string;
  project_id: string | null;
  task_id: string | null;
  seconds: number;
  billable_seconds: number;
  notes: string[];
};
type Named = { id: string; code: string | null; name: string };

// The small annotations on a line. Times, dates and IDs ("09:12 → 10:00",
// "S1") are mono; words with a figure ("away 40m", "corrected") are `note`, in
// the UI face, since in mono they read as code (critique 2026-10-06).
const meta: CSSProperties = {
  fontSize: 'var(--hf-text-2xs)',
  fontFamily: 'var(--hf-font-mono)',
  color: 'var(--hf-ink-3)',
};
const note: CSSProperties = {
  fontSize: 'var(--hf-text-2xs)',
  color: 'var(--hf-ink-3)',
  fontVariantNumeric: 'tabular-nums',
};
const line: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--hf-space-2)',
};

export function DayClockLog({
  profileId,
  reloadKey,
  onChanged,
  onFix,
  onEditDay,
}: {
  profileId: string;
  /** Change it to refetch — e.g. after the parent files a correction or a claim. */
  reloadKey?: unknown;
  /**
   * Pass only on the viewer's OWN log: it enables Cancel on pending requests and
   * is called after one. HR's copy decides in Requests & approvals instead.
   */
  onChanged?: () => void;
  /**
   * Pass only when the viewer may set someone ELSE's times (HR, owner
   * 2026-10-13): an auto-closed day then offers Set times beside its badge.
   * The server decides; this only offers it.
   */
  onFix?: (day: { entryId: string; workDate: string; checkIn: string }) => void;
  /**
   * Pass when Edit day is on for this viewer: every day offers Edit day, and
   * the card offers Edit a day (null: they pick it) for a day with no row.
   */
  onEditDay?: (dayKey: string | null) => void;
}) {
  const toast = useToast();
  const [entries, setEntries] = useState<DayClockEntry[]>([]);
  const [att, setAtt] = useState<Attendance[]>([]);
  const [leaves, setLeaves] = useState<LeaveSpan[]>([]);
  const [pauses, setPauses] = useState<PauseRow[]>([]);
  const [typeShort, setTypeShort] = useState<Record<string, string>>({});
  const [corrections, setCorrections] = useState<CorrectionRow[]>([]);
  const [closedMonths, setClosedMonths] = useState<ReadonlySet<string>>(new Set());
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [dayEdits, setDayEdits] = useState<DayEditRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [alloc, setAlloc] = useState<AllocRow[]>([]);
  const [named, setNamed] = useState<Record<string, Named>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 'loading' only until the first read lands: the card opened on "No check-ins,
  // absences or requests in this window" and then filled in. A read that errors
  // is 'failed', never that sentence (TS-14).
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [winOffset, setWinOffset] = useState(0); // 0 = most recent 14 days, 1 = prior fortnight…
  const [tz, setTz] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  });

  const WIN_DAYS = 14;
  const now = Date.now();
  // The window's most-recent day and its 14-day start, as ORG-local keys: the
  // same keys work_date, date and attendance.date carry, and the rows' buckets
  // (docs/UI-CONVENTIONS.md §2). Browser keys lost org-today's rows off the edge.
  const baseKey = addDaysToKey(orgDateKey(now, tz), -winOffset * WIN_DAYS);
  const sinceKey = addDaysToKey(baseKey, -(WIN_DAYS - 1));

  useEffect(() => {
    if (!supabase) return;
    supabase
      .from('org_settings')
      .select('tz_iana')
      .maybeSingle()
      .then(({ data }) => {
        const z = (data as { tz_iana: string | null } | null)?.tz_iana;
        if (z) setTz(z);
      });
    void listCategories().then(setCategories);
  }, []);

  useEffect(() => {
    if (!supabase) return;
    const sb = supabase;
    let active = true;
    // timestamptz bounds over-fetch a day each side; entries are bucketed (and
    // trimmed to the window) by orgDateKey below.
    const since = addDays(new Date(`${sinceKey}T00:00:00`), -1).toISOString();
    const until = addDays(new Date(`${baseKey}T00:00:00`), 2).toISOString();
    // One Promise.all, so one answer. These were seven independent `data ?? []`
    // chains: an outage on any one drew days with no pauses, no leave or no
    // requests, and said nothing.
    void Promise.all([
      sb
        .from('time_entries')
        .select('id, created_at, started_at, ended_at, seconds, status, system_closed')
        .eq('user_id', profileId)
        .is('project_id', null)
        .gte('created_at', since)
        .lt('created_at', until)
        .order('created_at', { ascending: true }),
      sb
        .from('attendance')
        .select('profile_id, date, status, hours')
        .eq('profile_id', profileId)
        .gte('date', sinceKey)
        .lte('date', baseKey),
      sb
        .from('leave_requests')
        .select('start_date, end_date, leave_type_id')
        .eq('profile_id', profileId)
        .eq('status', 'approved')
        .gte('end_date', sinceKey)
        .lte('start_date', baseKey),
      sb
        .from('time_entry_pauses')
        .select('id, entry_id, paused_at, resumed_at')
        .eq('user_id', profileId)
        .gte('paused_at', since)
        .lt('paused_at', until)
        .order('paused_at', { ascending: true }),
      sb
        .from('time_corrections')
        .select(
          'id, entry_id, work_date, current_seconds, requested_seconds, requested_in, requested_out, reason, status',
        )
        .eq('profile_id', profileId)
        .gte('work_date', sinceKey)
        .lte('work_date', baseKey)
        .order('created_at', { ascending: true }),
      sb
        .from('compensated_hours')
        .select('id, date, hours, category, status')
        .eq('profile_id', profileId)
        .gte('date', sinceKey)
        .lte('date', baseKey)
        .order('created_at', { ascending: true }),
      sb.from('leave_types').select('id, short'),
      sb
        .from('day_edits')
        .select('id, work_date, kind, status, reason, decline_reason, proposed, applied')
        .eq('profile_id', profileId)
        .gte('work_date', sinceKey)
        .lte('work_date', baseKey)
        .order('created_at', { ascending: true }),
      // Months whose days can no longer be set directly (20261013100000).
      sb.from('payroll_period').select('year, month').neq('status', 'open'),
      // Project-timer split per day, only while the module is on in this build.
      isModuleEnabled('projects')
        ? sb
            .from('time_allocation')
            .select('work_date, project_id, task_id, seconds, billable_seconds, notes')
            .eq('user_id', profileId)
            .gte('work_date', sinceKey)
            .lte('work_date', baseKey)
        : Promise.resolve({ data: [], error: null }),
    ]).then(async (res) => {
      if (!active) return;
      if (res.some((r) => r.error)) {
        setStatus('failed');
        return;
      }
      const [te, at, lv, pa, co, cl, lt, de, pp, al] = res;
      const allocRows = (al.data as AllocRow[]) ?? [];
      // Names for the split. RLS may hide a project from HR who isn't on it;
      // that row then says "A project" rather than failing the history.
      const pids = [...new Set(allocRows.flatMap((a) => (a.project_id ? [a.project_id] : [])))];
      const tids = [...new Set(allocRows.flatMap((a) => (a.task_id ? [a.task_id] : [])))];
      const [pn, tn] = await Promise.all([
        pids.length
          ? sb.from('projects').select('id, code, name').in('id', pids)
          : Promise.resolve({ data: [] }),
        tids.length
          ? sb.from('tasks').select('id, code, name:title').in('id', tids)
          : Promise.resolve({ data: [] }),
      ]);
      if (!active) return;
      const names: Record<string, Named> = {};
      [...((pn.data as Named[]) ?? []), ...((tn.data as Named[]) ?? [])].forEach(
        (n) => (names[n.id] = n),
      );
      setAlloc(allocRows);
      setNamed(names);
      setClosedMonths(
        new Set(
          ((pp.data as { year: number; month: number }[]) ?? []).map(
            (p) => `${p.year}-${String(p.month).padStart(2, '0')}`,
          ),
        ),
      );
      setEntries((te.data as DayClockEntry[]) ?? []);
      setAtt((at.data as Attendance[]) ?? []);
      setLeaves((lv.data as LeaveSpan[]) ?? []);
      setPauses((pa.data as PauseRow[]) ?? []);
      setCorrections((co.data as CorrectionRow[]) ?? []);
      setClaims((cl.data as ClaimRow[]) ?? []);
      setDayEdits((de.data as DayEditRow[]) ?? []);
      const map: Record<string, string> = {};
      ((lt.data as { id: string; short: string }[]) ?? []).forEach((t) => (map[t.id] = t.short));
      setTypeShort(map);
      setStatus('ready');
    });
    return () => {
      active = false;
    };
  }, [profileId, sinceKey, baseKey, reloadKey, attempt]);

  const byDay = new Map<string, DayClockEntry[]>();
  entries.forEach((e) => {
    const k = orgDateKey(e.created_at, tz);
    if (k < sinceKey || k > baseKey) return; // the over-fetched margin
    byDay.set(k, [...(byDay.get(k) ?? []), e]);
  });

  // Leave / attendance context per day — disambiguates a long mid-day gap
  // (just a break) from a sanctioned absence (leave / holiday / wfh).
  const attByDay: Record<string, Attendance> = {};
  att.forEach((a) => (attByDay[a.date] = a));
  const leaveOn = (k: string): LeaveSpan | undefined =>
    leaves.find((l) => l.start_date <= k && k <= l.end_date);
  const pausesByEntry: Record<string, PauseRow[]> = {};
  pauses.forEach((p) => (pausesByEntry[p.entry_id] = [...(pausesByEntry[p.entry_id] ?? []), p]));
  // work_date / date are org-local days — the same keys orgDateKey produces.
  const corrByDay: Record<string, CorrectionRow[]> = {};
  corrections.forEach((c) => (corrByDay[c.work_date] = [...(corrByDay[c.work_date] ?? []), c]));
  const claimsByDay: Record<string, ClaimRow[]> = {};
  claims.forEach((c) => (claimsByDay[c.date] = [...(claimsByDay[c.date] ?? []), c]));
  const allocByDay: Record<string, AllocRow[]> = {};
  alloc.forEach((a) => (allocByDay[a.work_date] = [...(allocByDay[a.work_date] ?? []), a]));
  // Approving a correction rewrites `seconds` but leaves the span alone, so the
  // removed time would otherwise read as a pause (and inflate "away").
  const correctedIds = new Set(
    corrections.filter((c) => c.status === 'approved').map((c) => c.entry_id),
  );
  const editsByDay: Record<string, DayEditRow[]> = {};
  dayEdits.forEach((e) => (editsByDay[e.work_date] = [...(editsByDay[e.work_date] ?? []), e]));
  const hasRequests = (k: string) =>
    !!(corrByDay[k]?.length || claimsByDay[k]?.length || editsByDay[k]?.length);

  // ── The day as a ledger (owner, 2026-10-09) ────────────────────────────────
  // The right-hand column adds up to the day's total: each session as it was
  // CLOCKED, each approved edit as its signed change, each approved credit as
  // its hours. So a session an edit changed shows what was clocked, and the
  // edit's line beside it shows the difference — 7:00:00 and −4:00:00, never a
  // badge the reader has to do sums around. Waiting and declined requests keep
  // their badge and count for nothing.
  const approvedByEntry = new Map<string, CorrectionRow[]>();
  for (const c of corrections)
    if (c.status === 'approved' && c.entry_id)
      approvedByEntry.set(c.entry_id, [...(approvedByEntry.get(c.entry_id) ?? []), c]);
  const signed = (secs: number) => `${secs < 0 ? '−' : '+'}${fmtDuration(Math.abs(secs), 'clock')}`;
  const creditedOn = (k: string) =>
    (claimsByDay[k] ?? [])
      .filter((c) => c.status === 'approved')
      .reduce((sum, c) => sum + Math.round(c.hours * 3600), 0);

  const days = [...byDay.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, list]) => {
      const activeEntry = list.find((e) => e.status === 'running' || e.status === 'paused') ?? null;
      const totalWorked = list.reduce((s, e) => s + elapsedOf(e, now), 0);
      const firstIn = list[0].created_at;
      const lastLogged = [...list].reverse().find((e) => e.ended_at) ?? null;
      const lastOut = activeEntry ? null : (lastLogged?.ended_at ?? null);
      // "Away" = everything between first check-in and last check-out (or now,
      // while live) that wasn't worked — covers pauses AND checkout gaps alike.
      const endRefMs = activeEntry ? now : lastOut ? new Date(lastOut).getTime() : now;
      const away = Math.max(
        0,
        Math.round((endRefMs - new Date(firstIn).getTime()) / 1000) - totalWorked,
      );
      return {
        key,
        firstIn,
        lastOut,
        activeEntry,
        totalWorked,
        // A corrected session's span no longer means anything, so neither does away.
        away: list.some((e) => correctedIds.has(e.id)) ? 0 : away,
        systemClosed: list.some((e) => e.system_closed),
        // Compliance review signals: a >10h day, or a single >5h stretch with no
        // recorded break (Factories/Shops-Act rest-break norm).
        overCap: totalWorked > 10 * 3600,
        noBreak: list.some(
          (e) => elapsedOf(e, now) > 5 * 3600 && (pausesByEntry[e.id]?.length ?? 0) === 0,
        ),
        sessions: list,
      };
    });

  // Days with a sanctioned absence (leave / holiday / absent) or a request but no
  // clock activity still get a row — so an approved leave reads as leave, never as
  // a suspicious gap or missed day, and a missed-time request has somewhere to live.
  const clockless: typeof days = [];
  for (let i = 0; i < WIN_DAYS; i++) {
    const k = addDaysToKey(baseKey, -i);
    if (byDay.has(k)) continue;
    const a = attByDay[k];
    const lv = leaveOn(k);
    if (!lv && !hasRequests(k) && (!a || a.status === 'present' || a.status === 'wfh')) continue;
    clockless.push({
      key: k,
      firstIn: '',
      lastOut: null,
      activeEntry: null,
      totalWorked: 0,
      away: 0,
      systemClosed: false,
      overCap: false,
      noBreak: false,
      sessions: [],
    });
  }
  const allDays = [...days, ...clockless].sort((a, b) => b.key.localeCompare(a.key));

  // Day context chip: approved leave wins, then the attendance status.
  const ctxOf = (
    k: string,
  ): { tone: 'warning' | 'danger' | 'sky' | 'neutral'; text: string } | null => {
    const lv = leaveOn(k);
    if (lv)
      return {
        tone: 'warning',
        text: `${typeShort[lv.leave_type_id] ?? 'leave'} · approved leave`,
      };
    const a = attByDay[k];
    if (a?.status === 'leave') return { tone: 'warning', text: 'leave' };
    if (a?.status === 'absent') return { tone: 'danger', text: 'absent' };
    if (a?.status === 'holiday') return { tone: 'neutral', text: 'holiday' };
    if (a?.status === 'wfh') return { tone: 'sky', text: 'wfh' };
    return null;
  };

  // fmtDur lived here ("1h 05m" / "40m") and seeded lib/duration's 'hm' style.

  // A cancelled request is deleted, so drop it here rather than wait on a refetch.
  const cancel = async (kind: 'correction' | 'claim' | 'day', id: string) => {
    setBusy(id);
    setErr(null);
    try {
      if (kind === 'correction') {
        await cancelCorrection(id);
        setCorrections((cs) => cs.filter((c) => c.id !== id));
      } else if (kind === 'day') {
        const { error } = await cancelDayEdit(id);
        if (error) throw error;
        setDayEdits((es) => es.filter((e) => e.id !== id));
      } else {
        const { error } = await cancelClaim(id);
        if (error) throw error;
        setClaims((cs) => cs.filter((c) => c.id !== id));
      }
      onChanged?.();
      // The line vanishing was the only sign it had worked.
      toast.push({
        tone: 'success',
        message:
          kind === 'correction'
            ? 'Time correction cancelled.'
            : kind === 'day'
              ? 'Day change request cancelled.'
              : 'Compensated-hours claim cancelled.',
      });
    } catch (e) {
      setErr((e as { message?: string }).message ?? 'Could not cancel that request.');
    } finally {
      setBusy(null);
    }
  };

  // A pending request's Cancel is a small text button a phone task hangs on.
  // Under a finger it is 44px tall for real, not a .hf-hit overlay: a day with
  // two pending requests stacks their lines 4px apart, and the second overlay
  // took the foot of the first Cancel (UI-CONVENTIONS §8).
  const coarse = useMediaQuery('(pointer: coarse)');

  // One request under its day, laid out like a session line with the status at the end.
  const reqLine = (r: {
    id: string;
    kind: 'correction' | 'claim' | 'day';
    label: string;
    mono: string;
    text: string;
    // A correction is never 'revoked' — only compensated hours can be — but the
    // two share this one render, and CorrectionRow['status'] is already a
    // subset of CompHours['status'], so the wider one alone covers both.
    status: CompHours['status'];
    /** Its signed figure in the day's column, when it counts toward the total. */
    figure?: string;
  }) => (
    <div key={r.id} style={line}>
      <span style={{ width: 20, flexShrink: 0, display: 'inline-flex' }}>
        <Icon
          name={r.kind === 'claim' ? 'award' : 'flag'}
          size={11}
          style={{ color: 'var(--hf-ink-3)' }}
        />
      </span>
      <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>{r.label}</span>
      <Mono style={{ fontSize: 'var(--hf-text-2xs)' }}>{r.mono}</Mono>
      {/* The reason wraps. It was cut to one line with the rest in a `title`,
          which no keyboard or touch screen can open (§4). */}
      <span
        style={{
          flex: '1 1 12ch',
          minWidth: 0,
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
          overflowWrap: 'anywhere',
        }}
      >
        {r.text}
      </span>
      {/* On your own log a waiting request says on whom (ME-16). HR's copy of
          someone's log says only "waiting": "your manager" would be the
          viewer's, and HR is who decides the rest. */}
      {r.figure ? (
        <Mono
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--hf-text-2xs)',
            color: 'var(--hf-sky-text)',
          }}
        >
          {r.figure}
        </Mono>
      ) : (
        <RequestStatus
          status={r.status}
          kind={
            onChanged
              ? r.kind === 'claim'
                ? 'comp'
                : r.kind === 'day'
                  ? 'day'
                  : 'correction'
              : undefined
          }
        />
      )}
      {r.status === 'pending' && onChanged && (
        <Button
          variant="ghost"
          size="sm"
          loading={busy === r.id}
          style={coarse ? { minHeight: 44 } : undefined}
          onClick={() => void cancel(r.kind, r.id)}
        >
          Cancel
        </Button>
      )}
    </div>
  );

  // Where the day's worked time went, by project timer (owner, 2026-10-14):
  // each project or issue with its billable part, then the unallocated rest.
  // A breakdown of the sessions above, so its figures sit in the line, not in
  // the right-hand column that adds up to the day. Only on a day with a timer.
  const allocLines = (rows: AllocRow[]) => {
    const timed = rows.filter((a) => a.project_id);
    if (!timed.length) return null;
    const rest = rows.find((a) => !a.project_id)?.seconds ?? 0;
    const label = (a: AllocRow) =>
      [a.project_id, a.task_id]
        .map((id) => id && named[id])
        .filter((n): n is Named => !!n)
        .map((n) => (n.code ? `${n.code} ${n.name}` : n.name))
        .join(' · ') || 'A project';
    return (
      <>
        {timed.map((a) => (
          <div key={`${a.project_id}:${a.task_id}`} style={line}>
            <span style={{ width: 20, flexShrink: 0, display: 'inline-flex' }}>
              <Icon name="folder" size={11} style={{ color: 'var(--hf-ink-3)' }} />
            </span>
            <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-2)' }}>
              {label(a)}
            </span>
            <span style={note}>
              {fmtDuration(a.seconds, 'hm')} ·{' '}
              {a.billable_seconds >= a.seconds
                ? 'billable'
                : a.billable_seconds > 0
                  ? `${fmtDuration(a.billable_seconds, 'hm')} billable`
                  : 'not billable'}
            </span>
            {a.notes.length > 0 && (
              <span
                style={{ ...note, flexBasis: '100%', paddingLeft: 28, overflowWrap: 'anywhere' }}
              >
                {a.notes.join(' · ')}
              </span>
            )}
          </div>
        ))}
        {rest >= 60 && (
          <div style={line}>
            <span style={{ width: 20, flexShrink: 0 }} />
            <span style={note}>unallocated {fmtDuration(rest, 'hm')} · not billable</span>
          </div>
        )}
      </>
    );
  };

  return (
    <Card
      // "Day clock" also names the card at the top of Timesheets (TS-20).
      label="Check-in history"
      right={
        // Two weeks at a time, so PeriodStepper's day/week/month units do not
        // name it; the buttons are named for what they do.
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
          }}
        >
          {onEditDay && (
            <Button size="sm" variant="secondary" icon="plus" onClick={() => onEditDay(null)}>
              Edit a day
            </Button>
          )}
          <span aria-live="polite" style={meta}>
            {fmtDayKey(sinceKey)} – {fmtDayKey(baseKey)}
          </span>
          <IconButton
            icon="chevronLeft"
            label="Earlier two weeks"
            variant="secondary"
            onClick={() => setWinOffset((w) => w + 1)}
          />
          <IconButton
            icon="chevronRight"
            label="Later two weeks"
            variant="secondary"
            disabled={winOffset === 0}
            focusableWhenDisabled
            onClick={() => setWinOffset((w) => Math.max(0, w - 1))}
          />
        </div>
      }
    >
      {err && (
        <div style={{ marginBottom: 'var(--hf-space-2)' }}>
          <InlineStatus tone="error">{err}</InlineStatus>
        </div>
      )}
      {status === 'failed' ? (
        <LoadError what="the check-in history" onRetry={() => setAttempt((n) => n + 1)} />
      ) : status === 'loading' ? (
        <div role="status" style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
          Loading…
        </div>
      ) : allDays.length === 0 ? (
        <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
          No check-ins, absences or requests in this window.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
          {allDays.map((d) => {
            const ctx = ctxOf(d.key);
            const dayCorr = corrByDay[d.key] ?? [];
            const dayClaims = claimsByDay[d.key] ?? [];
            const dayChanges = editsByDay[d.key] ?? [];
            return (
              <div
                key={d.key}
                style={{
                  border: '1px solid var(--hf-rule)',
                  borderRadius: 'var(--hf-radius-md)',
                  padding: 'var(--hf-space-2) var(--hf-space-3)',
                  background: 'var(--hf-surface)',
                }}
              >
                {/* Day header — context + span + away + worked total */}
                <div style={line}>
                  <span
                    style={{
                      fontSize: 'var(--hf-text-sm)',
                      color: 'var(--hf-ink)',
                      fontWeight: 'var(--hf-weight-medium)',
                      minWidth: 88,
                    }}
                  >
                    {dayLabel(d.key)}
                  </span>
                  {ctx && (
                    <Badge tone={ctx.tone} dot>
                      {ctx.text}
                    </Badge>
                  )}
                  {/* Clock times in the ORG's zone, like the day they sit under:
                      a 09:30 arrival in the org read 04:00 to a viewer abroad,
                      beneath an org-local date. */}
                  {d.sessions.length > 0 ? (
                    <Mono dim style={{ fontSize: 'var(--hf-text-2xs)' }}>
                      {fmtTime(d.firstIn, tz)} → {d.activeEntry ? '…' : fmtTime(d.lastOut, tz)}
                    </Mono>
                  ) : (
                    <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>
                      no clock activity
                    </span>
                  )}
                  {d.activeEntry && (
                    <Badge tone={d.activeEntry.status === 'running' ? 'accent' : 'warning'} dot>
                      {d.activeEntry.status === 'running' ? 'on the clock' : 'on break'}
                    </Badge>
                  )}
                  {d.systemClosed && (
                    <Badge tone="danger" dot>
                      auto-closed · review
                    </Badge>
                  )}
                  {/* Offered only when the server would accept it: no request
                      waiting on that day, and its payroll month still open. */}
                  {d.systemClosed &&
                    onFix &&
                    !dayCorr.some((c) => c.status === 'pending') &&
                    !closedMonths.has(d.key.slice(0, 7)) &&
                    (() => {
                      const closed = d.sessions.find((e) => e.system_closed);
                      return closed ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="hf-hit"
                          onClick={() =>
                            onFix({
                              entryId: closed.id,
                              workDate: d.key,
                              checkIn: closed.created_at,
                            })
                          }
                        >
                          Set times
                        </Button>
                      ) : null;
                    })()}
                  {onEditDay && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="hf-hit"
                      onClick={() => onEditDay(d.key)}
                    >
                      Edit day
                    </Button>
                  )}
                  {d.overCap && (
                    <Badge tone="warning" dot>
                      over 10h
                    </Badge>
                  )}
                  {d.noBreak && (
                    <Badge tone="warning" dot>
                      no break
                    </Badge>
                  )}
                  {/* margin-left: auto, not a flex: 1 spacer: a spacer collapses
                      when the line wraps (every busy day at 320), and the total
                      fell to the left under the date. */}
                  <span style={{ ...line, marginLeft: 'auto' }}>
                    {d.away >= 60 && <span style={note}>away {fmtDuration(d.away, 'hm')}</span>}
                    {(d.sessions.length > 0 || creditedOn(d.key) > 0) && (
                      // Worked plus approved credited hours: the column below adds up to it.
                      <Mono style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
                        {fmtDuration(d.totalWorked + creditedOn(d.key), 'clock')}
                      </Mono>
                    )}
                  </span>
                </div>

                {/* Sessions — one row per check-in → check-out cycle — then the
                    day's correction requests and compensated-hours claims */}
                {(d.sessions.length > 0 ||
                  dayCorr.length > 0 ||
                  dayClaims.length > 0 ||
                  dayChanges.length > 0) && (
                  <div
                    style={{
                      marginTop: 'var(--hf-space-2)',
                      marginLeft: 'var(--hf-space-1)',
                      paddingLeft: 'var(--hf-space-3)',
                      borderLeft: '2px solid var(--hf-rule-2)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--hf-space-1)',
                    }}
                  >
                    {d.sessions.map((s, i) => {
                      const live = s.status === 'running' || s.status === 'paused';
                      const worked = elapsedOf(s, now);
                      const sessionPauses = pausesByEntry[s.id] ?? [];
                      const corrected = correctedIds.has(s.id);
                      // Aggregate paused time inside the session (span − worked) —
                      // shown only when no detailed pause rows exist (older data
                      // predating the pause log).
                      const spanEnd = s.ended_at ? new Date(s.ended_at).getTime() : now;
                      const pausedInside = Math.max(
                        0,
                        Math.round((spanEnd - new Date(s.created_at).getTime()) / 1000) - worked,
                      );
                      return (
                        <div
                          key={s.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--hf-space-1)',
                          }}
                        >
                          <div style={line}>
                            <span style={{ ...meta, width: 20, flexShrink: 0 }}>S{i + 1}</span>
                            <Mono dim style={{ fontSize: 'var(--hf-text-2xs)' }}>
                              {fmtTime(s.created_at, tz)} →{' '}
                              {live
                                ? s.status === 'running'
                                  ? 'now'
                                  : 'on break'
                                : fmtTime(s.ended_at, tz)}
                            </Mono>
                            {corrected && (
                              <span style={{ ...note, color: 'var(--hf-sky-text)' }}>
                                corrected
                              </span>
                            )}
                            {!corrected && sessionPauses.length === 0 && pausedInside >= 60 && (
                              <span style={{ ...note, color: 'var(--hf-amber-text)' }}>
                                paused {fmtDuration(pausedInside, 'hm')}
                              </span>
                            )}
                            <Mono
                              style={{
                                marginLeft: 'auto',
                                fontSize: 'var(--hf-text-2xs)',
                                color: 'var(--hf-ink-2)',
                              }}
                            >
                              {/* As clocked: the edit's own line carries the change. */}
                              {fmtDuration(
                                approvedByEntry.get(s.id)?.[0]?.current_seconds ?? worked,
                                'clock',
                              )}
                            </Mono>
                          </div>
                          {/* Each pause, with its actual boundaries */}
                          {sessionPauses.map((p) => {
                            const open = !p.resumed_at;
                            const dur = Math.max(
                              0,
                              Math.round(
                                ((open ? now : new Date(p.resumed_at as string).getTime()) -
                                  new Date(p.paused_at).getTime()) /
                                  1000,
                              ),
                            );
                            return (
                              <div key={p.id} style={{ ...line, paddingLeft: 28 }}>
                                <Icon
                                  name="pause"
                                  size={9}
                                  style={{ color: 'var(--hf-amber-text)', flexShrink: 0 }}
                                />
                                <span style={meta}>
                                  {fmtTime(p.paused_at, tz)} →{' '}
                                  {open ? 'now' : fmtTime(p.resumed_at, tz)}
                                </span>
                                <span style={{ ...note, color: 'var(--hf-amber-text)' }}>
                                  {open ? 'on break · ' : ''}
                                  {fmtDuration(dur, 'hm')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    {/* "7h 00m → 3h 19m": it was "7:00 → 3:19" two lines under a
                        session reading 3:19:00, where 3:19 was minutes (TS-03). */}
                    {dayCorr.map((c) =>
                      reqLine({
                        id: c.id,
                        kind: 'correction',
                        label: 'time correction',
                        mono: correctionSummary(c),
                        text: c.reason,
                        status: c.status,
                        // Only an edit to a session shown above: a missed session
                        // it added is already its own line.
                        figure:
                          c.status === 'approved' &&
                          c.entry_id &&
                          d.sessions.some((x) => x.id === c.entry_id)
                            ? signed(c.requested_seconds - c.current_seconds)
                            : undefined,
                      }),
                    )}
                    {dayClaims.map((c) =>
                      reqLine({
                        id: c.id,
                        kind: 'claim',
                        label: 'compensated hours',
                        mono: `+${fmtHours(c.hours)}`,
                        text: categories.find((k) => k.id === c.category)?.label ?? c.category,
                        status: c.status,
                        figure:
                          c.status === 'approved' ? signed(Math.round(c.hours * 3600)) : undefined,
                      }),
                    )}
                    {/* A day change is already in the sessions above, so it
                        carries no figure: it says what was set, and why. */}
                    {dayChanges.map((e) => {
                      const adjusted =
                        e.kind === 'request' &&
                        e.status === 'approved' &&
                        !!e.applied &&
                        !!e.proposed.sessions &&
                        sessionsText(e.applied.sessions) !== sessionsText(e.proposed.sessions);
                      return reqLine({
                        id: e.id,
                        kind: 'day',
                        label:
                          e.kind === 'direct'
                            ? 'day changed for you'
                            : adjusted
                              ? 'day change, approved with changes'
                              : 'day change',
                        mono: e.applied
                          ? sessionsText(e.applied.sessions)
                          : e.proposed.sessions
                            ? sessionsText(e.proposed.sessions)
                            : '',
                        text:
                          e.status === 'rejected' && e.decline_reason
                            ? `${e.reason} · Declined: ${e.decline_reason}`
                            : e.reason,
                        status: e.status,
                      });
                    })}
                    {allocLines(allocByDay[d.key] ?? [])}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
